/**
 * Residual TARA v2 — pure reducer.
 *
 * The hook layer (`useResidualProgress`) owns all async + lifecycle
 * effects (initial fetch, phase polling, evidence polling, auto-start
 * risk_analysis, completion signal) and dispatches into here. The
 * reducer is the projection: backend phase + data + UI selection.
 *
 * Loop-back detection: when the backend transitions from
 * risk_decisions/completed back to controls_pending (because the
 * user picked "Reduce" on a risk that needs a new control), set
 * `loopBackPending = true`. The flag is cleared via CLEAR_LOOP_BACK
 * when the user dismisses the banner or adds a control.
 */
import {
  EvidenceStatus,
  PHASE_TO_RAIL_STOP,
  RailStop,
  ResidualData,
  ResidualPhase,
  ResidualState,
} from './types';

export type ResidualAction =
  | { type: 'HYDRATE_FROM_BACKEND'; payload: ResidualData | null }
  | { type: 'SET_SELECTED_STOP'; payload: RailStop }
  | { type: 'SET_EVIDENCE_STATUS'; payload: EvidenceStatus | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_LOOP_BACK' }
  | { type: 'RESET' };

export function residualReducer(
  state: ResidualState,
  action: ResidualAction,
): ResidualState {
  switch (action.type) {
    case 'HYDRATE_FROM_BACKEND': {
      const data = action.payload;
      if (!data) {
        // Null payload = backend returned 404 (no session). Leave
        // `error` alone so a SET_ERROR from a concurrent auto-init
        // (which races this fetch on the first mount) doesn't get
        // wiped. The success path below still clears error since
        // populated data implies the workflow is healthy.
        return { ...state, data: null, backendPhase: null };
      }
      const newBackendPhase = data.current_phase as ResidualPhase;
      const newRailStop = PHASE_TO_RAIL_STOP[newBackendPhase];

      // Loop-back detection: backend used to be past Risk Decisions
      // and now reports controls_pending → a Reduce decision must
      // have cycled the workflow back. Sticky until cleared.
      const wasPostDecision =
        state.backendPhase === 'risk_decisions' ||
        state.backendPhase === 'completed';
      const loopBackTriggered =
        wasPostDecision && newBackendPhase === 'controls_pending';

      return {
        ...state,
        data,
        backendPhase: newBackendPhase,
        // Auto-select the backend's stop on first hydrate
        // (selectedStop is null until then). After that, user
        // selection is sticky — backend updates don't snap the UI
        // back to the active stop.
        selectedStop: state.selectedStop ?? newRailStop,
        loopBackPending: state.loopBackPending || loopBackTriggered,
        error: null,
        // Backend-persisted agent failure — surfaced as a retry card
        // in the affected phase. Cleared server-side by /continue.
        analysisError: data.error_message ?? null,
      };
    }

    case 'SET_SELECTED_STOP':
      return { ...state, selectedStop: action.payload };

    case 'SET_EVIDENCE_STATUS':
      return { ...state, evidenceStatus: action.payload };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'CLEAR_LOOP_BACK':
      return { ...state, loopBackPending: false };

    case 'RESET':
      return {
        selectedStop: null,
        backendPhase: null,
        data: null,
        evidenceStatus: null,
        loopBackPending: false,
        loading: false,
        error: null,
        analysisError: null,
      };

    default:
      return state;
  }
}
